import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useDateFormat from "commons/hooks/useDateFormat";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import {
    Column,
    FilterFieldCollection,
    FilterOperation,
    FilterType,
    SortDirection,
} from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";
import HtmlWrapper from "commons/ui/HtmlWrapper";

import { makeUrl } from "commons/utils/http";
import { createUnixMilliFromLocalDate } from "commons/utils/moment";

import useNewsApi from "hooks/api/private/useNewsApi";
import useSnackbar from "hooks/useSnackbar";

import { NewsListItem } from "types/api/news";

export type NewsListHook = {
    navigateToNewNews: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dataTableHook: DataTableHook<NewsListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useNewsList(): NewsListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    description: {
                        key: "description",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    slug: {
                        key: "slug",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "news-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.title = value;
                newForm.search.description = value;
                newForm.search.slug = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            media: {
                key: "media",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            enabled: {
                key: "enabled",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "news-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllNews, deleteNews } = useNewsApi();
    const { formatDate } = useDateFormat();

    const navigate = useNavigate();

    const navigateToEditNews = React.useCallback(
        (row: NewsListItem) => {
            navigate(makeUrl(":newsId/modifica", { newsId: row.id }));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina News",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteNewsButton = React.useCallback(
        (row: NewsListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la News?", () => {
                const [apiCall] = deleteNews({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("News eliminata con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione della news"));
            });
        },
        [
            enqueueSuccessSnackbar,
            setTriggerReloadTable,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteNews,
        ],
    );

    const columns: Array<Column<NewsListItem>> = React.useMemo(
        () => [
            {
                id: "date",
                header: "Data",
                width: "90px",
                orderable: true,
                render: (row: NewsListItem) => formatDate(createUnixMilliFromLocalDate(row.date)),
            },
            {
                id: "news_languages.title",
                header: "Titolo",
                render: (row: NewsListItem) => <HtmlWrapper htmlString={row.title} />,
                orderable: true,
            },
            {
                id: "enabled",
                header: "Area pubblica",
                width: "130px",
                render: (row: NewsListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: NewsListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditNews}
                        onDelete={deleteNewsButton}
                    />
                ),
            },
        ],
        [navigateToEditNews, deleteNewsButton, formatDate],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<NewsListItem>({
        apiCall: getAllNews,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        sortDefault: {
            col: "date",
            dir: SortDirection.Desc,
        },
        size: 20,
        key: "news-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewNews = React.useCallback(() => {
        navigate("/news/nuova");
    }, [navigate]);

    return React.useMemo(
        (): NewsListHook => ({
            onSearchFilterChange,
            navigateToNewNews,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            onSearchFilterChange,
            navigateToNewNews,
            filterSearchHook,
            alertConfirmHook,
            staticFilterHook,
            dataTableHook,
        ],
    );
}

export default useNewsList;
